import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/button";
import { useRovingRadioGroup } from "@/shared/hooks/use-roving-radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { DateField } from "@/shared/ui/date-field";
import { FormField } from "@/shared/ui/form-field";
import { Label } from "@/shared/ui/label";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import { parseDecimalToAmountScale } from "@/shared/lib/amount";
import { composeReferenceTime } from "@/shared/lib/date-utils";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import type { ScopeContext } from "@/features/auth";
import { createExternalTrades } from "@/features/portfolio/api/external-trade";
import { accountScopeOf } from "@/features/portfolio/api/portfolio";
import type { Instrument } from "@/features/portfolio/api/instrument";
import { InstrumentPicker } from "./InstrumentPicker";

interface AddTradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioId: string;
  onRecorded: () => void;
}

const newUniqueId = () => crypto.randomUUID();

const TRADE_SIDES = ["buy", "sell"] as const;

const TRADE_FIELD_MAP: Record<string, string> = {
  instrument_id: "instrument",
  symbol_amount: "quantity",
  symbol_scale: "quantity",
  price_amount: "price",
  price_scale: "price",
  reference_time: "referenceTime",
};

export const AddTradeDialog = ({ open, onOpenChange, portfolioId, onRecorded }: AddTradeDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null = sessionId
    ? ScopeContextStorage.getScopeContext(sessionId)
    : null;
  const accountScope = scopeContext ? accountScopeOf(scopeContext) : null;

  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [side, setSide] = useState<"buy" | "sell">("buy");
  const { radioProps: sideRadioProps } = useRovingRadioGroup(TRADE_SIDES, side, setSide);
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [referenceTime, setReferenceTime] = useState("");
  const [referenceTimeOfDay, setReferenceTimeOfDay] = useState("");
  const [uniqueId, setUniqueId] = useState(newUniqueId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));

  const resetTradeEntry = () => {
    setInstrument(null);
    setSide("buy");
    setQuantity("");
    setPrice("");
    setReferenceTime("");
    setReferenceTimeOfDay("");
    setFieldErrors({});
    setUniqueId(newUniqueId());
  };

  useEffect(() => {
    if (open) resetTradeEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    const nextFieldErrors: Record<string, string> = {};
    const parsedQuantity = parseDecimalToAmountScale(quantity);
    const parsedPrice = parseDecimalToAmountScale(price);
    if (!instrument) nextFieldErrors.instrument = t("portfolio.instrumentRequired");
    if (!parsedQuantity || parsedQuantity.amount <= 0) {
      nextFieldErrors.quantity = t("portfolio.quantityInvalid");
    }
    if (!parsedPrice || parsedPrice.amount < 0) {
      nextFieldErrors.price = t("portfolio.priceInvalid");
    }
    if (!referenceTime) nextFieldErrors.referenceTime = t("portfolio.referenceTimeRequired");
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    if (!instrument || !parsedQuantity || !parsedPrice) return;
    if (!sessionId || !accountScope) {
      toast({ title: t("portfolio.noAccountScope"), variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      await createExternalTrades(sessionId, accountScope, [{
        portfolio_id: portfolioId,
        instrument_id: instrument.id,
        side,
        symbol_amount: parsedQuantity.amount,
        symbol_scale: parsedQuantity.scale,
        price_amount: parsedPrice.amount,
        price_scale: parsedPrice.scale,
        reference_time: composeReferenceTime(referenceTime, referenceTimeOfDay),
        unique_id: uniqueId,
      }]);
      toast({ title: t("portfolio.tradeRecorded") });
      resetTradeEntry();
      onRecorded();
    } catch (err) {
      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        TRADE_FIELD_MAP,
      );
      setFieldErrors(backendFieldErrors);
      const message = isUnreadableResponseError(err)
        ? t("errors.unreadableResponse")
        : generalError;
      if (message) {
        toast({ title: message, variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("portfolio.addTradeTitle")}</DialogTitle>
          <DialogDescription>{t("portfolio.addTradeDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="instrument-search">{t("portfolio.instrument")}</Label>
            <InstrumentPicker
              value={instrument}
              onChange={(next) => {
                setInstrument(next);
                clearFieldError("instrument");
              }}
              error={fieldErrors.instrument}
            />
          </div>

          <div className="space-y-1">
            <Label id="trade-side-label">{t("portfolio.side")}</Label>
            <div className="flex gap-2" role="radiogroup" aria-labelledby="trade-side-label">
              {TRADE_SIDES.map((option) => (
                <Button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={side === option}
                  variant={side === option ? "default" : "outline"}
                  data-testid={`trade-side-${option}`}
                  onClick={() => setSide(option)}
                  {...sideRadioProps(option)}
                >
                  {t(`portfolio.side.${option}`)}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="trade-quantity"
              label={t("portfolio.quantity")}
              inputMode="decimal"
              value={quantity}
              error={fieldErrors.quantity}
              onChange={(value: string) => {
                setQuantity(value);
                clearFieldError("quantity");
              }}
            />
            <FormField
              id="trade-price"
              label={t("portfolio.price")}
              inputMode="decimal"
              value={price}
              error={fieldErrors.price}
              onChange={(value: string) => {
                setPrice(value);
                clearFieldError("price");
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label id="trade-currency-label">{t("portfolio.currency")}</Label>
              <p
                aria-labelledby="trade-currency-label"
                data-testid="trade-currency-display"
                className="flex h-10 items-center rounded-md border border-border bg-muted px-3 text-sm"
              >
                {instrument ? instrument.currency : t("portfolio.currencyFromInstrument")}
              </p>
            </div>
            <DateField
              id="trade-reference-time"
              label={t("portfolio.referenceTime")}
              value={referenceTime}
              error={fieldErrors.referenceTime}
              onChange={(value: string) => {
                setReferenceTime(value);
                clearFieldError("referenceTime");
              }}
            />
            <FormField
              id="trade-reference-time-of-day"
              label={t("portfolio.referenceTimeOfDay")}
              type="time"
              value={referenceTimeOfDay}
              onChange={setReferenceTimeOfDay}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("portfolio.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="trade-submit">
            {isSubmitting ? t("portfolio.recording") : t("portfolio.record")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
